import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { lddStateObject, setTrustShieldIframe } from '../../redux/reducer';
import { getUserManager } from './oidcManager';
import { initTrustShieldSSO } from './initTrustShieldSSO';
import { fetchMatterDefaultAccount } from './trustShieldService';
import { buildTrustShieldUrl } from './trustShieldUtils';
import { TransferType } from './trustShieldTypes';

export interface TransferFormLaunchPayload {
    appId: number;
    fileNumber: string;
    clientName: string;
    fileCreationDate: string;
    // Optional pre-fill for the transfer amount (integer cents). Built up on the
    // transfer modal (typed directly and/or by adding file items). Omitted/0 →
    // the trust transfer surface starts at $0 exactly as before. Always editable
    // on the trust side regardless.
    amountCents?: number;
    // Which transfer surface the user picked on the modal's pill. Forwarded so the
    // trust side opens the matching screen directly instead of defaulting to
    // matter-to-matter. Omitted only by the legacy bridge (older puracct builds).
    transferType?: TransferType;
}

/**
 * Launch TrustShield's Direct Transfer surface (matter-to-matter OR trust-to-trust)
 * with this file pre-selected as the source. Shared by the compat bridge
 * (`topWindowTransferForm`, kept for older puracct builds) and the new
 * TransferItemSelectionModal so neither duplicates the SSO / exportId / iframe
 * wiring.
 *
 * Mints a single-use exportId, stashes the payload in localStorage, and opens the
 * embedded iframe via the shared SSO URL builder. Resolving the file's trust
 * account is best-effort — omitted on failure, in which case the user picks the
 * account/source matter manually and the transfer still works.
 */
export function useLaunchTransferForm(): (payload: TransferFormLaunchPayload) => Promise<void> {
    const dispatch = useAppDispatch();
    const SESSION_INFO = useAppSelector((g: lddStateObject) => g.SessionInfo);

    return useCallback(async (payload: TransferFormLaunchPayload) => {
        const exportId = crypto.randomUUID();

        let bankAccountId: string | undefined;
        try {
            const sid = SESSION_INFO?.sid;
            const sub = sid ? await initTrustShieldSSO(sid) : null;
            const user = sub ? await getUserManager(sub).getUser() : null;
            const jwt = user && !user.expired ? user.access_token : null;
            if (jwt) bankAccountId = (await fetchMatterDefaultAccount(jwt, payload.appId)) ?? undefined;
        } catch (err) {
            console.warn('[TrustShield] transfer: could not resolve trust account; user will pick manually:', err);
        }

        // Only forward a positive amount — a $0/absent total leaves the payload
        // byte-identical to the pre-selection-screen flow.
        const hasAmount = Number.isFinite(payload.amountCents) && (payload.amountCents as number) > 0;

        localStorage.setItem(`realtiExport_${exportId}`, JSON.stringify({
            payload: {
                action: 'transfer-form',
                appId: payload.appId,
                fileNumber: payload.fileNumber,
                clientName: payload.clientName,
                fileCreationDate: payload.fileCreationDate,
                ...(hasAmount ? { amountCents: payload.amountCents } : {}),
                ...(payload.transferType ? { transferType: payload.transferType } : {}),
                ...(bankAccountId ? { bank_account_id: bankAccountId } : {}),
            },
        }));

        const url = buildTrustShieldUrl(
            { action: 'transfer-form', exportId, embedded: 'true' },
            { sid: SESSION_INFO?.sid },
        );
        dispatch(setTrustShieldIframe({ url, title: 'Direct Transfer', exportId }));
    }, [dispatch, SESSION_INFO?.sid]);
}
